import { useState } from "react";
import { Loader2, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { admin, filterBtn, inputCls } from "./adminApi";
import { PaymentInstructionsPanel } from "./PaymentInstructionsPanel";

const CARRIERS = [
  ["auspost", "Australia Post"], ["startrack", "StarTrack"], ["sendle", "Sendle"], ["dhl", "DHL Express"],
  ["fedex", "FedEx"], ["ups", "UPS"], ["other", "Other Courier"],
];

function FulfilModal({ sessionId, onClose, onDone }) {
  const [carrier, setCarrier] = useState("auspost");
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await admin.post(`/admin/orders/${sessionId}/fulfil`, { carrier, tracking_number: tracking.trim() || null });
      toast.success(tracking.trim() ? "Marked shipped with tracking" : "Marked fulfilled");
      onDone();
    } catch { toast.error("Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-6" data-testid="fulfil-modal">
      <div className="bl-glass border border-white/12 max-w-md w-full p-8 space-y-5">
        <div>
          <div className="bl-eyebrow text-[color:var(--bl-gold)]">Ship Order</div>
          <div className="font-display text-white text-2xl uppercase mt-2">Add Tracking</div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mt-2 truncate">{sessionId}</div>
        </div>
        <div className="space-y-3">
          <div>
            <div className="bl-eyebrow text-white/60 mb-2">Carrier</div>
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="fulfil-carrier"
              className={`w-full bg-black ${inputCls} px-3 py-3 text-xs`}>
              {CARRIERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <div className="bl-eyebrow text-white/60 mb-2">Tracking Number</div>
            <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Paste tracking number"
              data-testid="fulfil-tracking" className={`w-full ${inputCls} px-3 py-3 text-xs`} />
            <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-white/40">Leave blank to mark packed only</div>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="bl-btn flex-1 text-[11px] py-2" data-testid="fulfil-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="fulfil-submit"
            className="bl-btn gold flex-1 text-[11px] py-2 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={13} className="animate-spin" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderActions({ o, onOpenFulfil, onQuick, onMarkPaid }) {
  if (o.payment_status !== "paid") {
    return (
      <div className="mt-3 flex flex-col items-end gap-2">
        <button onClick={() => onMarkPaid(o.session_id)} data-testid={`mark-paid-${o.session_id}`} className="bl-btn gold text-[10px] py-2 px-3">Mark Paid</button>
        <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/40">Awaiting crypto payment</div>
      </div>
    );
  }
  if (o.tracking_number) {
    return (
      <div className="mt-3 text-right">
        <div className="inline-flex items-center gap-1 text-[color:var(--bl-green)] font-mono text-[10px] tracking-[0.25em] uppercase">
          <CheckCircle2 size={13} /> Shipped
        </div>
        <div className="font-mono text-[10px] text-white/50 mt-1">{o.carrier || "courier"} · {o.tracking_number}</div>
      </div>
    );
  }
  if (o.fulfilled) {
    return (
      <div className="mt-3 flex flex-col items-end gap-2">
        <div className="inline-flex items-center gap-1 text-[color:var(--bl-green)] font-mono text-[10px] tracking-[0.25em] uppercase">
          <CheckCircle2 size={13} /> Packed
        </div>
        <button onClick={() => onOpenFulfil(o.session_id)} data-testid={`fulfil-track-${o.session_id}`} className="bl-btn text-[10px] py-1.5 px-3">Add Tracking</button>
      </div>
    );
  }
  return (
    <div className="mt-3 flex flex-col items-end gap-2">
      <button onClick={() => onOpenFulfil(o.session_id)} data-testid={`fulfil-${o.session_id}`} className="bl-btn gold text-[10px] py-2 px-3">Ship + Tracking</button>
      <button onClick={() => onQuick(o.session_id)} data-testid={`fulfil-quick-${o.session_id}`}
        className="text-white/50 hover:text-white/80 font-mono text-[9px] tracking-[0.2em] uppercase">Mark packed only</button>
    </div>
  );
}

function OrderRow({ o, onOpenFulfil, onQuick, onMarkPaid }) {
  const s = o.shipping;
  return (
    <div data-testid={`admin-order-${o.session_id}`} className="border border-white/8 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{new Date(o.created_at).toLocaleString()}</div>
          <div className="font-mono text-xs text-white/70 mt-1 truncate">{o.session_id}</div>
          <div className="mt-3 space-y-1">
            {(o.items || []).map((i) => (
              <div key={`${o.session_id}-${i.product_id}-${i.variant_id}`} className="text-white/90 text-sm">
                {i.name} <span className="text-[color:var(--bl-gold)]">· {i.strength}</span> · ×{i.quantity} · 10-vial pack
              </div>
            ))}
          </div>
          {s ? (
            <div className="mt-4 font-mono text-[11px] text-white/70 leading-relaxed">
              <div className="bl-eyebrow text-white/40 mb-1">Ship to</div>
              {s.full_name}<br/>
              {s.email} {s.phone ? `· ${s.phone}` : ""}<br/>
              {s.line1}{s.line2 ? `, ${s.line2}` : ""}<br/>
              {s.city}, {s.state} {s.postcode}<br/>
              {s.country}
            </div>
          ) : <div className="mt-4 font-mono text-[11px] text-red-300">No shipping details captured</div>}
          {o.promo && (
            <div className="mt-3 font-mono text-[11px] text-[color:var(--bl-gold)]">Promo: {o.promo.code} · saved A${o.promo.discount_amount.toFixed(2)}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-white text-xl">A${(o.amount || 0).toFixed(2)}</div>
          <div className="mt-1 font-mono text-[10px] tracking-[0.25em] uppercase text-white/50">{o.payment_status}</div>
          <OrderActions o={o} onOpenFulfil={onOpenFulfil} onQuick={onQuick} onMarkPaid={onMarkPaid} />
        </div>
      </div>
    </div>
  );
}

export default function AdminOrders({ orders, filter, setFilter, search, setSearch, refresh }) {
  const [fulfilFor, setFulfilFor] = useState(null);

  const markPaid = async (sid) => {
    if (!window.confirm(`Confirm crypto payment received for ${sid}? This emails the customer's receipt and adjusts stock.`)) return;
    try {
      await admin.post(`/admin/orders/${sid}/mark-paid`, {});
      toast.success("Order marked paid — receipt sent");
      await refresh();
    } catch { toast.error("Action failed"); }
  };

  const fulfilQuick = async (sid) => {
    try {
      await admin.post(`/admin/orders/${sid}/fulfil`, {});
      toast.success("Marked fulfilled");
      await refresh();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="space-y-4">
      <PaymentInstructionsPanel />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="admin-order-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, session, city, promo…" className={`w-full ${inputCls} pl-9 pr-3 py-2.5 text-xs`} />
        </div>
        <div className="flex gap-1.5">
          {["all", "paid", "unfulfilled", "pending"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`} className={filterBtn(filter === f)}>{f}</button>
          ))}
        </div>
      </div>

      <div className="space-y-3" data-testid="admin-orders-panel">
        {orders.length === 0 && <div className="text-white/40 py-8 text-center">No orders match this view</div>}
        {orders.map(o => <OrderRow key={o.session_id} o={o} onOpenFulfil={setFulfilFor} onQuick={fulfilQuick} onMarkPaid={markPaid} />)}
      </div>

      {fulfilFor && (
        <FulfilModal sessionId={fulfilFor} onClose={() => setFulfilFor(null)}
          onDone={async () => { setFulfilFor(null); await refresh(); }} />
      )}
    </div>
  );
}
